import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Badge,
  Button,
  Table,
  Spinner,
  Alert,
  Tabs,
  Tab,
  Form,
  InputGroup,
  Modal,
  Container,
  ListGroup,
  Pagination,
  Dropdown
} from "react-bootstrap";
import Footer from "../layouts/Footer";
import { useAuth } from "../hooks/useAuth";
import axios from "axios";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function EntiteDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { business } = useAuth();

  const [loading, setLoading] = useState(true);
  const [entite, setEntite] = useState(null);
  
  // États pour les deux soldes
  const [soldeV1, setSoldeV1] = useState(0);
  const [soldeV2, setSoldeV2] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  const [activeTab, setActiveTab] = useState('overview');

  // ================ STYLE ORIGINAL DES TARIFS ================
  const [tarifs, setTarifs] = useState([]);
  const [tarifsLoading, setTarifsLoading] = useState(false);
  const [tarifsError, setTarifsError] = useState(null);
  const [tarifsRefreshLoading, setTarifsRefreshLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('country');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedTarifs, setSelectedTarifs] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);

  // ================ FORMULAIRE DE RECHARGE SMS ================
  const [nombreSMSInput, setNombreSMSInput] = useState('');
  const [rechargeLoading, setRechargeLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [notification, setNotification] = useState({ show: false, message: "", type: "info" });
  const [responseData, setResponseData] = useState(null);
  const [smsQuantity, setSmsQuantity] = useState(0);

  // ================ FORMULAIRE DE RECHARGE CRÉDIT (V2) ================
  const [montantInput, setMontantInput] = useState('');
  const [rechargeCreditLoading, setRechargeCreditLoading] = useState(false);
  const [showCreditSuccessModal, setShowCreditSuccessModal] = useState(false);
  const [showCreditErrorModal, setShowCreditErrorModal] = useState(false);
  const [creditErrorMessage, setCreditErrorMessage] = useState('');
  const [creditSuccessMessage, setCreditSuccessMessage] = useState('');
  const [creditResponseData, setCreditResponseData] = useState(null);
  const [creditMontant, setCreditMontant] = useState(0);

  // ================ MODAL KYC ================
  const [showKycModal, setShowKycModal] = useState(false);
  const [kycLoading, setKycLoading] = useState(false);
  const [kycAction, setKycAction] = useState('');

  // ================ HISTORIQUE SMS ================
  const [smsHistory, setSmsHistory] = useState([]);
  const [smsHistoryLoading, setSmsHistoryLoading] = useState(false);
  const [smsHistoryError, setSmsHistoryError] = useState(null);
  const [selectedSms, setSelectedSms] = useState(null);
  const [showSmsDetailModal, setShowSmsDetailModal] = useState(false);

  // Modal d'édition
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  // Alertes et notifications
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });

  useEffect(() => {
    if (id) {
      loadEntiteDetails();
      fetchTarifs();
    }
  }, [id]);

  // Charger l'historique quand l'onglet est activé
  useEffect(() => {
    if (activeTab === 'sms-history' && id) {
      fetchSmsHistory();
    }
  }, [activeTab, id]);

  const loadEntiteDetails = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return navigate('/login');

      const response = await apiClient.get(`/entites/${id}`);
      console.log('📦 Données entité:', JSON.stringify(response.data, null, 2));
      
      setEntite(response.data.client);
      
      const balanceData = response.data.balance;
      if (balanceData && balanceData.length > 0) {
        const balanceItem = balanceData[0];
        setSoldeV1(parseFloat(balanceItem.balance_1) || 0);
        setSoldeV2(parseFloat(balanceItem.balance) || 0);
        setLastUpdate(balanceItem.last_update || null);
      } else {
        setSoldeV1(0);
        setSoldeV2(0);
        setLastUpdate(null);
      }
      
      setEditForm(response.data.client);
    } catch (error) {
      console.error(' Erreur chargement:', error);
      showAlert('Erreur lors du chargement des données', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // ================ RÉCUPÉRATION DE L'HISTORIQUE SMS ================
  const fetchSmsHistory = async () => {
    if (!id) return;
    
    setSmsHistoryLoading(true);
    setSmsHistoryError(null);
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error("Token manquant");
      
      const response = await apiClient.get(`/entites/${id}/sms`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { limit: 10 }
      });
      
      console.log('📦 Historique SMS:', JSON.stringify(response.data, null, 2));
      
      if (response.data.code === 200) {
        setSmsHistory(response.data.data || []);
      } else {
        setSmsHistory([]);
      }
    } catch (error) {
      console.error(' Erreur historique SMS:', error);
      setSmsHistoryError(error.message);
      setSmsHistory([]);
    } finally {
      setSmsHistoryLoading(false);
    }
  };

  // ================ FONCTION DE MISE À JOUR KYC ================
  const handleUpdateKyc = async () => {
    console.log("🔄 DÉBUT updateKYC - action:", kycAction, "id:", id);
    
    setKycLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error("Token d'authentification manquant");
      }
        
      const kycValue = kycAction === 'activate' ? 'OK' : 'KO';
      const requestData = { acc_type: "business", kyc: kycValue };
      
      console.log("📤 Envoi:", requestData);
      
      const response = await apiClient.put(`/entites/kyc/${id}`, requestData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      
      console.log("📥 Réponse:", response.status, response.data);
      
      if (response.status === 200 || response.status === 201) {
        setEntite(prev => ({ ...prev, kyc: kycValue }));
        setShowKycModal(false);
        showAlert(` KYC ${kycValue === 'OK' ? 'activé' : 'désactivé'} avec succès`, 'success');
        await loadEntiteDetails();
      } else {
        throw new Error(response.data?.message || `Erreur ${response.status}`);
      }
      
    } catch (error) {
      console.error(" ERREUR:", error);
      
      let errorMsg = "Erreur lors de la mise à jour KYC";
      
      if (error.response) {
        console.error("  Status:", error.response.status);
        console.error("  Data:", error.response.data);
        errorMsg = error.response.data?.message || `Erreur ${error.response.status}`;
      } else if (error.request) {
        errorMsg = "Impossible de contacter le serveur";
      } else {
        errorMsg = error.message;
      }
      
      showAlert(` ${errorMsg}`, 'danger');
    } finally {
      setKycLoading(false);
      console.log("🔄 FIN updateKYC");
    }
  };

  // ================ MISE À JOUR DU CLIENT ================
  const openEditModal = () => {
    setEditForm({ ...entite });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    setEditLoading(true);
    try {
      const payload = {
        business_name: editForm.business_name || null,
        business_email: editForm.business_email || null,
        phone: editForm.phone || null,
        business_country: editForm.business_country || null,
        business_city: editForm.business_city || null,
        x_header_client: editForm.x_header_client?.trim() || null,
      };

      const response = await apiClient.put(`/entites/${id}`, payload);

      if (response.status === 200 && response.data?.code === 200) {
        setEntite(prev => ({ ...prev, ...(response.data.data || payload) }));
        setShowEditModal(false);
        showAlert('Client mis à jour avec succès', 'success');
      } else {
        throw new Error(response.data?.message || `Erreur ${response.status}`);
      }
    } catch (error) {
      let errorMsg = "Erreur lors de la mise à jour du client";
      const data = error.response?.data;
      if (data?.errors) {
        errorMsg = Object.values(data.errors).flat().join(' ');
      } else if (data?.message) {
        errorMsg = data.message;
      } else if (error.request && !error.response) {
        errorMsg = "Impossible de contacter le serveur";
      } else if (error.message) {
        errorMsg = error.message;
      }
      showAlert(errorMsg, 'danger');
    } finally {
      setEditLoading(false);
    }
  };

  // ================ FONCTIONS POUR LES TARIFS ================
  const fetchTarifs = async (showLoader = false) => {
    try {
      if (showLoader) {
        setTarifsRefreshLoading(true);
      } else {
        setTarifsLoading(true);
      }
      setTarifsError(null);
      
      const response = await apiClient.get('/pricing/list');
      const result = response.data;
      
      if (result.code === 200 && result.data) {
        setTarifs(result.data);
      } else {
        throw new Error(result.message || 'Erreur lors de la récupération des données');
      }
    } catch (err) {
      setTarifsError("Erreur de chargement des tarifs");
    } finally {
      setTarifsLoading(false);
      setTarifsRefreshLoading(false);
    }
  };

  const showNotification = (message, type = "info") => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "info" });
    }, 5000);
  };

  // Gestion de la saisie du nombre de SMS
  const handleNombreSMSChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*$/.test(value)) {
      setNombreSMSInput(value);
      setSmsQuantity(value ? parseInt(value) : 0);
    }
  };

  // Gestion de la saisie du montant (Recharge Crédit V2)
  const handleMontantChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setMontantInput(value);
      setCreditMontant(value ? parseFloat(value) : 0);
    }
  };

  // ================ FONCTION DE RECHARGE SMS ================
  const handleRechargeSolde = async () => {
    const smsQuantity = parseInt(nombreSMSInput);
    if (!smsQuantity || smsQuantity <= 0) {
      showNotification("Veuillez saisir un nombre de SMS valide", "warning");
      return;
    }

    setRechargeLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      
      const requestData = {
        sms_quantity: smsQuantity,
        entity_id: parseInt(id)
      };

      const response = await apiClient.post(
        '/recharge-sms',
        requestData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const responseData = response.data;
      
      if (responseData.code === 200) {
        const data = responseData.data || {};
        const nouveauSolde = data.nouveau_solde || data.solde_apres || 0;
        
        setResponseData(responseData);
        setShowSuccessModal(true);
        setSoldeV1(nouveauSolde);
        
        setTimeout(() => {
          setShowSuccessModal(false);
          setNombreSMSInput('');
          setSmsQuantity(0);
          loadEntiteDetails();
        }, 5000);
        
        showNotification(
          ` ${smsQuantity} SMS ajoutés avec succès. Nouveau solde: ${nouveauSolde} SMS`,
          "success"
        );
        
      } else {
        const errorMsg = responseData.message || 
                         responseData.error || 
                         "Erreur lors de la recharge";
        throw new Error(errorMsg);
      }
      
    } catch (error) {
      console.error(" Erreur recharge SMS:", error);
      
      let errorMsg = "Erreur lors de la recharge";
      
      if (error.response) {
        errorMsg = error.response.data?.message || 
                   error.response.data?.error || 
                   `Erreur ${error.response.status}`;
        console.error("📌 Détails de l'erreur:", error.response.data);
      } else if (error.request) {
        errorMsg = "Impossible de contacter le serveur";
      } else {
        errorMsg = error.message;
      }
      
      setErrorMessage(errorMsg);
      setShowErrorModal(true);
    } finally {
      setRechargeLoading(false);
    }
  };

  // ================ FONCTION DE RECHARGE CRÉDIT (V2) ================
  const handleRechargeCredit = async () => {
    const montant = parseFloat(montantInput);
    if (!montant || montant <= 0) {
      showNotification("Veuillez saisir un montant valide", "warning");
      return;
    }

    setRechargeCreditLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      
      const requestData = {
        sms_credits: montant,
        entity_id: parseInt(id)
      };

      const response = await apiClient.post(
        '/recharge-credit',
        requestData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        const responseData = response.data;
        
        if (responseData.success === true) {
          setCreditSuccessMessage(responseData.message || 'Crédits ajoutés avec succès');
          setCreditResponseData(responseData);
          setShowCreditSuccessModal(true);
          
          if (responseData.nouveau_solde !== undefined) {
            setSoldeV2(responseData.nouveau_solde);
          }
          
          setTimeout(() => {
            setShowCreditSuccessModal(false);
            setMontantInput('');
            setCreditMontant(0);
            loadEntiteDetails();
          }, 5000);
          
          showNotification(
            ` ${montant} crédits ajoutés avec succès.`,
            "success"
          );
          
        } else {
          const errorMsg = responseData.message || "Erreur lors de la recharge crédit";
          throw new Error(errorMsg);
        }
        
      } else {
        const errorMsg = response.data?.message || `Erreur ${response.status}`;
        throw new Error(errorMsg);
      }
      
    } catch (error) {
      console.error(" Erreur recharge crédit:", error);
      
      let errorMsg = "Erreur lors de la recharge crédit";
      
      if (error.response) {
        const data = error.response.data;
        errorMsg = data?.message || data?.error || `Erreur ${error.response.status}`;
        console.error("📌 Détails de l'erreur:", data);
      } else if (error.request) {
        errorMsg = "Impossible de contacter le serveur";
      } else {
        errorMsg = error.message || "Une erreur inattendue s'est produite";
      }
      
      setCreditErrorMessage(errorMsg);
      setShowCreditErrorModal(true);
    } finally {
      setRechargeCreditLoading(false);
    }
  };

  const showAlert = (message, type = 'warning') => {
    setAlertMessage({ show: true, message, type });
    setTimeout(() => setAlertMessage({ show: false, message: '', type: 'info' }), 5000);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const formatSmsDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      '200': { variant: 'success', label: 'Reçu' },
      'SENT': { variant: 'success', label: 'SENT' },
      
      '201': { variant: 'primary', label: 'Envoyé' },
      '202': { variant: 'warning', label: 'En attente' },
      '400': { variant: 'danger', label: 'Échec' },
      'FAILED': { variant: 'danger', label: 'FAILED' },
      '500': { variant: 'danger', label: 'Erreur' }
    };
    const defaultStatus = { variant: 'secondary', label: status || 'Inconnu' };
    return statusMap[status] || defaultStatus;
  };

  // ================ FONCTIONS POUR L'EXPORT DES TARIFS ================
  const formatTarifData = (tarif) => ({
    id: tarif.id,
    reference: `TARIF-${tarif.country}-${tarif.network}`,
    type: "SMS",
    country: tarif.country,
    network: tarif.network,
    sms_pricing: tarif.sms_pricing,
    currency: tarif.currency,
    statut: "Actif"
  });

  const filteredAndSortedItems = useMemo(() => {
    if (!tarifs.length) return [];
    const formatted = tarifs.map(formatTarifData);
    
    let filtered = formatted.filter(item =>
      item.reference.toLowerCase().includes(filterText.toLowerCase()) ||
      item.country.toLowerCase().includes(filterText.toLowerCase()) ||
      item.network.toLowerCase().includes(filterText.toLowerCase())
    );

    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      if (sortField === 'sms_pricing') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }
      return sortDirection === 'asc' ? (aValue > bValue ? 1 : -1) : (aValue < bValue ? 1 : -1);
    });

    return filtered;
  }, [tarifs, filterText, sortField, sortDirection]);

  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedTarifs(currentItems.map(tarif => tarif.id));
    } else {
      setSelectedTarifs([]);
    }
  };

  const handleSelectTarif = (tarifId) => {
    setSelectedTarifs(prev => 
      prev.includes(tarifId) 
        ? prev.filter(id => id !== tarifId)
        : [...prev, tarifId]
    );
  };

  const isAllSelected = currentItems.length > 0 && selectedTarifs.length === currentItems.length;
  const isSomeSelected = selectedTarifs.length > 0 && selectedTarifs.length < currentItems.length;

  const handleExport = (format) => {
    setExportLoading(true);
    const dataToExport = selectedTarifs.length > 0
      ? tarifs.filter(tarif => selectedTarifs.includes(tarif.id)).map(formatTarifData)
      : filteredAndSortedItems;

    if (!dataToExport.length) { 
      alert("Aucune donnée à exporter"); 
      setExportLoading(false); 
      return; 
    }

    if (format === "csv") {
      const headers = ["Référence","Type","Pays","Réseau","Prix SMS","Devise","Statut"];
      const csvContent = [
        "\uFEFF" + headers.join(";"),
        ...dataToExport.map(p => [
          p.reference, p.type, p.country, p.network, p.sms_pricing, p.currency, p.statut
        ].map(v=>`"${v}"`).join(";"))
      ].join("\n");
      downloadFile(csvContent, "text/csv;charset=utf-8;", `tarifs-${Date.now()}.csv`);

    } else if (format === "excel") {
      const ws = XLSX.utils.json_to_sheet(dataToExport.map(p => ({
        "Référence": p.reference,
        "Type": p.type,
        "Pays": p.country,
        "Réseau": p.network,
        "Prix SMS": p.sms_pricing,
        "Devise": p.currency,
        "Statut": p.statut
      })));
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Tarifs SMS");
      XLSX.writeFile(wb, `tarifs-${Date.now()}.xlsx`);

    } else if (format === "json") {
      downloadFile(JSON.stringify(dataToExport, null, 2), "application/json;charset=utf-8;", `tarifs-${Date.now()}.json`);

    } else if (format === "pdf") {
      generatePDF(dataToExport);
    }

    setExportLoading(false);
  };

  const generatePDF = (data) => {
    const doc = new jsPDF();
    
    const primaryColor = [41, 128, 185];
    const headerColor = [240, 240, 240];
    
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 25, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("TARIFS DES SERVICES SMS", 105, 15, { align: 'center' });
    
    doc.setFontSize(9);
    doc.text(`Export généré le: ${new Date().toLocaleDateString('fr-FR')}`, 15, 22);
    doc.text(`Total: ${data.length} tarif(s)`, 180, 22, { align: 'right' });
    
    const pageWidth = 210;
    const margin = 10;
    let yPosition = 35;
    
    const headers = ["RÉFÉRENCE", "TYPE", "PAYS", "RÉSEAU", "PRIX", "DEVISE", "STATUT"];
    const colWidths = [35, 20, 25, 25, 20, 20, 25];
    
    doc.setFillColor(...headerColor);
    doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 8, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    
    let xPosition = margin;
    headers.forEach((header, index) => {
      doc.text(header, xPosition + 2, yPosition);
      xPosition += colWidths[index];
    });
    
    yPosition += 6;
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    
    data.forEach((item, index) => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
      }
      
      const rowData = [
        item.reference,
        item.type,
        item.country,
        item.network,
        item.sms_pricing.toString(),
        item.currency,
        item.statut
      ];
      
      let currentX = margin;
      rowData.forEach((cell, cellIndex) => {
        doc.text(cell, currentX + 2, yPosition);
        currentX += colWidths[cellIndex];
      });
      
      yPosition += 6;
    });
    
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    doc.save(`tarifs-sms-${timestamp}.pdf`);
  };

  const downloadFile = (content, type, filename) => {
    const blob = new Blob([content], { type });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const SortableHeader = ({ field, children }) => (
    <th 
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => handleSort(field)}
      className="position-relative"
    >
      {children}
      {sortField === field && (
        <i 
          className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill ms-1`}
          style={{ fontSize: '12px' }}
        ></i>
      )}
    </th>
  );

  if (loading) {
    return (
      <div className="main main-app" style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="text-center" style={{ width: '100%' }}>
          <div className="position-relative d-inline-block mb-4">
            <div className="spinner-border text-primary" style={{ width: '5rem', height: '5rem', borderWidth: '0.3rem' }} role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            <div className="position-absolute top-50 start-50 translate-middle">
              <i className="bi bi-envelope-paper text-primary" style={{ fontSize: '1.5rem' }}></i>
            </div>
          </div>
          <h5 className="fw-bold text-primary">Chargement des informations</h5>
          <p className="text-muted small">Veuillez patienter...</p>
          <div className="mt-3">
            <div className="d-flex justify-content-center gap-1">
              <span className="dot-flashing"></span>
              <span className="dot-flashing" style={{ animationDelay: '0.2s' }}></span>
              <span className="dot-flashing" style={{ animationDelay: '0.4s' }}></span>
            </div>
          </div>
        </div>
        <style>{`
          .dot-flashing {
            display: inline-block;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #0d6efd;
            animation: dot-flashing 1s linear infinite;
          }
          
          @keyframes dot-flashing {
            0% { opacity: 0.2; transform: scale(0.8); }
            50% { opacity: 1; transform: scale(1.2); }
            100% { opacity: 0.2; transform: scale(0.8); }
          }
        `}</style>
      </div>
    );
  }

  if (!entite) {
    return (
      <div className="main main-app" style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6}>
              <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
                <div className="bg-danger py-2"></div>
                <Card.Body className="p-5 text-center">
                  <div className="mb-4">
                    <svg width="120" height="120" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mx-auto">
                      <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#dc3545" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="#dc3545" fillOpacity="0.1"/>
                      <path d="M2 17L12 22L22 17" stroke="#dc3545" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="#dc3545" fillOpacity="0.1"/>
                      <path d="M2 12L12 17L22 12" stroke="#dc3545" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="#dc3545" fillOpacity="0.1"/>
                      <circle cx="12" cy="12" r="2" fill="#dc3545" fillOpacity="0.3"/>
                      <path d="M12 8V12M12 16H12.01" stroke="#dc3545" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <h1 className="display-6 fw-bold mb-3">Oups ! Entité introuvable</h1>
                  <p className="text-muted mb-4">
                    Nous n'avons pas pu trouver l'entité que vous recherchez. 
                    Elle a peut-être été déplacée ou supprimée.
                  </p>
                  <div className="bg-light p-3 rounded-3 mb-4">
                    <div className="d-flex align-items-center justify-content-center gap-3">
                      <div className="text-start">
                        <div className="small text-muted mb-1">ID recherché :</div>
                        <code className="bg-white p-2 rounded border">{id}</code>
                      </div>
                      <div className="vr"></div>
                      <div className="text-start">
                        <div className="small text-muted mb-1">Statut :</div>
                        <span className="badge bg-danger bg-opacity-10 text-danger px-3 py-2">
                          <i className="bi bi-x-circle me-1"></i>
                          Non trouvé
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-3 justify-content-center">
                    <Button variant="outline-secondary" className="px-4 py-2 rounded-3" onClick={() => navigate(-1)}>
                      <i className="bi bi-arrow-left me-2"></i> Retour
                    </Button>
                    <Button variant="primary" className="px-4 py-2 rounded-3" onClick={() => navigate('/apps/Entites')}>
                      <i className="bi bi-building me-2"></i> Liste des entités
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <>
      <div className="main main-app p-4">
        <Container fluid className="px-0">
          {/* Alertes */}
          {alertMessage.show && (
            <Alert variant={alertMessage.type} dismissible onClose={() => setAlertMessage({ show: false })}>
              {alertMessage.message}
            </Alert>
          )}

          {/* Header avec navigation */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="d-flex align-items-center mb-3">
                    <Button
                      variant="light"
                      onClick={() => navigate(-1)}
                      className="me-3 rounded-circle"
                      style={{ width: '40px', height: '40px' }}
                    >
                      <i className="bi bi-arrow-left"></i>
                    </Button>
                    <div>
                      <h3 className="mb-1">
                        <span className="text-gradient-primary">Entité</span>
                        <span className="text-secondary ms-2">{entite.business_name}</span>
                      </h3>
                      <nav aria-label="breadcrumb">
                        <ol className="breadcrumb breadcrumb-arrow mb-0">
                          <li className="breadcrumb-item">
                            <Link to="/dashboard" className="text-decoration-none">Tableau de bord</Link>
                          </li>
                          <li className="breadcrumb-item">
                            <Link to="/apps/entite" className="text-decoration-none">Entités</Link>
                          </li>
                          <li className="breadcrumb-item active">Détails</li>
                        </ol>
                      </nav>
                    </div>
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <Button variant="outline-primary" onClick={openEditModal}>
                    <i className="bi bi-pencil me-2"></i>
                    Modifier
                  </Button>
                  
                  <Button 
                    variant={entite.kyc === 'OK' ? 'outline-danger' : 'outline-success'}
                    onClick={() => {
                      setKycAction(entite.kyc === 'OK' ? 'deactivate' : 'activate');
                      setShowKycModal(true);
                    }}
                  >
                    <i className={`bi ${entite.kyc === 'OK' ? 'bi-shield-slash' : 'bi-shield-check'} me-2`}></i>
                    {entite.kyc === 'OK' ? 'Désactiver KYC' : 'Activer KYC'}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Cartes statistiques */}
          <Row className="mb-4 g-4">
            <Col lg={3} md={6}>
              <Card className="border-0 shadow-lg overflow-hidden" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <Card.Body className="position-relative">
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <h6 className="text-uppercase mb-1 opacity-75">Solde Crédit SMS</h6>
                      <h4 className="mb-0">{new Intl.NumberFormat('fr-FR').format(soldeV1 || 0)}</h4>
                      <small className="opacity-75">
                        <i className="bi bi-clock me-1"></i>
                        {lastUpdate ? new Date(lastUpdate).toLocaleString('fr-FR') : 'N/A'}
                      </small>
                    </div>
                    <div className="icon-statistic bg-white text-dark bg-opacity-20">
                      <i className="bi bi-envelope-paper" style={{ fontSize: '2rem', display: 'inline-block' }}></i>
                    </div>
                  </div>
                  <div className="statistic-wave"></div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={3} md={6}>
              <Card className="border-0 shadow-lg overflow-hidden" style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white' }}>
                <Card.Body className="position-relative">
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <h6 className="text-uppercase mb-1 opacity-75">Solde Qte SMS</h6>
                      <h4 className="mb-0">{new Intl.NumberFormat('fr-FR').format(soldeV2 || 0)} SMS</h4>
                      <small className="opacity-75">
                        <i className="bi bi-clock me-1"></i>
                        {lastUpdate ? new Date(lastUpdate).toLocaleString('fr-FR') : 'N/A'}
                      </small>
                    </div>
                    <div className="icon-statistic bg-white text-dark bg-opacity-20">
                      <i className="bi bi-coin" style={{ fontSize: '2rem', display: 'inline-block' }}></i>
                    </div>
                  </div>
                  <div className="statistic-wave"></div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={3} md={6}>
              <Card className="border-0 shadow-lg overflow-hidden" style={{ 
                background: entite.kyc === 'OK' 
                  ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                  : 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)',
                color: 'white' 
              }}>
                <Card.Body className="position-relative">
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <h6 className="text-uppercase mb-1 opacity-75">Statut KYC</h6>
                      <h4 className="mb-0">{entite.kyc === 'OK' ? ' Validé' : ' En attente'}</h4>
                      <small className="opacity-75">
                        <i className="bi bi-shield-check me-1"></i>
                        {entite.kyc === 'OK' ? 'Compte vérifié' : 'Vérification requise'}
                      </small>
                    </div>
                    <div className="icon-statistic bg-white text-dark bg-opacity-20">
                      <i className="bi bi-shield-check" style={{ fontSize: '2rem', display: 'inline-block' }}></i>
                    </div>
                  </div>
                  <div className="statistic-wave"></div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={3} md={6}>
              <Card className="border-0 shadow-lg overflow-hidden" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                <Card.Body className="position-relative">
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <h6 className="text-uppercase mb-1 opacity-75">ID Business</h6>
                      <h4 className="mb-0">#{entite.business_id}</h4>
                      <small className="opacity-75">
                        <i className="bi bi-calendar-check me-1"></i>
                        Inscrit le {formatDate(entite.reg_date)}
                      </small>
                    </div>
                    <div className="icon-statistic bg-white text-dark bg-opacity-20">
                      <i className="bi bi-tag" style={{ fontSize: '2rem', display: 'inline-block' }}></i>
                    </div>
                  </div>
                  <div className="statistic-wave"></div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Onglets */}
          <Card className="border-0 shadow-lg">
            <Card.Header className="bg-white border-bottom p-0">
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="px-4 pt-3 custom-tabs"
              >
                {/* Onglet Aperçu */}
                <Tab
                  eventKey="overview"
                  title={
                    <div className="d-flex align-items-center px-3 py-2">
                      <i className="bi bi-building me-2"></i>
                      <span>Aperçu</span>
                    </div>
                  }
                >
                  <div className="p-4">
                    <Row>
                      <Col lg={6}>
                        <Card className="border-0 shadow-sm h-100">
                          <Card.Body>
                            <h6 className="card-title d-flex align-items-center mb-4">
                              <i className="bi bi-person me-2 text-primary"></i>
                              Informations générales
                            </h6>
                            <ListGroup variant="flush" className="list-group-custom">
                              <ListGroup.Item className="d-flex justify-content-between px-0 py-3 border-bottom">
                                <span className="text-muted">ID Business</span>
                                <Badge bg="light" text="dark" className="fw-bold px-3 py-2">
                                  {entite.business_id}
                                </Badge>
                              </ListGroup.Item>
                              <ListGroup.Item className="d-flex justify-content-between px-0 py-3 border-bottom">
                                <span className="text-muted">Responsable</span>
                                <span className="fw-medium">{entite.firstname} {entite.lastname}</span>
                              </ListGroup.Item>
                              <ListGroup.Item className="d-flex justify-content-between px-0 py-3 border-bottom">
                                <span className="text-muted">Type de compte</span>
                                <Badge bg={entite.acc_type === 'admin' ? 'primary' : 'success'} className="px-3 py-2">
                                  {entite.acc_type === 'admin' ? 'Administrateur' : 'Entreprise'}
                                </Badge>
                              </ListGroup.Item>
                              <ListGroup.Item className="d-flex justify-content-between px-0 py-3 border-bottom">
                                <span className="text-muted">Sender ID</span>
                                <Badge bg="info" className="px-3 py-2">{entite.sender || 'Non assigné'}</Badge>
                              </ListGroup.Item>
                              <ListGroup.Item className="d-flex justify-content-between px-0 py-3 border-bottom">
                                <span className="text-muted">Ident Header</span>
                                <span className="fw-medium">{entite.x_header_client || 'Non renseigné'}</span>
                              </ListGroup.Item>
                              <ListGroup.Item className="d-flex justify-content-between px-0 py-3">
                                <span className="text-muted">MFA</span>
                                <Badge bg={entite.has_mfa === 'YES' ? 'success' : 'secondary'} className="px-3 py-2">
                                  {entite.has_mfa === 'YES' ? 'Activé' : 'Désactivé'}
                                </Badge>
                              </ListGroup.Item>
                            </ListGroup>
                          </Card.Body>
                        </Card>
                      </Col>

                      <Col lg={6}>
                        <Card className="border-0 shadow-sm h-100">
                          <Card.Body>
                            <h6 className="card-title d-flex align-items-center mb-4">
                              <i className="bi bi-geo-alt me-2 text-primary"></i>
                              Localisation
                            </h6>
                            <ListGroup variant="flush" className="list-group-custom">
                              <ListGroup.Item className="d-flex align-items-start px-0 py-3 border-bottom">
                                <div className="flex-shrink-0">
                                  <div className="icon-circle bg-primary bg-opacity-10 text-primary">
                                    <i className="bi bi-geo-alt"></i>
                                  </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                  <h6 className="mb-1">Pays</h6>
                                  <p className="mb-0 text-muted">{entite.business_country || 'Non renseigné'}</p>
                                </div>
                              </ListGroup.Item>
                              <ListGroup.Item className="d-flex align-items-start px-0 py-3 border-bottom">
                                <div className="flex-shrink-0">
                                  <div className="icon-circle bg-success bg-opacity-10 text-success">
                                    <i className="bi bi-building"></i>
                                  </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                  <h6 className="mb-1">Ville</h6>
                                  <p className="mb-0 text-muted">{entite.business_city || 'Non renseignée'}</p>
                                </div>
                              </ListGroup.Item>
                              <ListGroup.Item className="d-flex align-items-start px-0 py-3 border-bottom">
                                <div className="flex-shrink-0">
                                  <div className="icon-circle bg-warning bg-opacity-10 text-warning">
                                    <i className="bi bi-telephone"></i>
                                  </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                  <h6 className="mb-1">Téléphone</h6>
                                  <p className="mb-0 text-muted">{entite.phone || 'Non renseigné'}</p>
                                </div>
                              </ListGroup.Item>
                              <ListGroup.Item className="d-flex align-items-start px-0 py-3">
                                <div className="flex-shrink-0">
                                  <div className="icon-circle bg-info bg-opacity-10 text-info">
                                    <i className="bi bi-envelope"></i>
                                  </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                  <h6 className="mb-1">Email</h6>
                                  <p className="mb-0 text-muted">{entite.business_email}</p>
                                </div>
                              </ListGroup.Item>
                            </ListGroup>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                </Tab>

                {/* Onglet Tarifs */}
                <Tab
                  eventKey="pricing"
                  title={
                    <div className="d-flex align-items-center px-3 py-2">
                      <i className="bi bi-tag me-2"></i>
                      <span>Tarifs</span>
                    </div>
                  }
                >
                  <div className="p-4">
                    {tarifsError && (
                      <Alert variant="danger" className="mb-4">
                        <div className="d-flex justify-content-between align-items-center">
                          <span><i className="bi bi-exclamation-triangle me-2"></i>{tarifsError}</span>
                          <Button variant="outline-danger" size="sm" onClick={() => fetchTarifs(true)}>Réessayer</Button>
                        </div>
                      </Alert>
                    )}

                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="fw-bold mb-0">
                        <i className="bi bi-table me-2 text-primary"></i>
                        Tableau des tarifs SMS
                      </h5>
                      <div className="d-flex gap-2">
                        <Dropdown>
                          <Dropdown.Toggle 
                            variant="primary" 
                            size="sm"
                            className="rounded-pill px-4"
                            disabled={exportLoading || tarifsLoading}
                          >
                            <i className={`bi ${exportLoading ? 'bi-arrow-repeat spin' : 'bi-download'} me-2`}></i>
                            {exportLoading ? 'Export...' : 'Exporter'}
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleExport('csv')}>
                              <i className="bi bi-file-earmark-spreadsheet me-2 text-success"></i> CSV
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleExport('excel')}>
                              <i className="bi bi-file-earmark-excel me-2 text-success"></i> Excel
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleExport('pdf')}>
                              <i className="bi bi-file-earmark-pdf me-2 text-danger"></i> PDF
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleExport('json')}>
                              <i className="bi bi-file-earmark-code me-2 text-warning"></i> JSON
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          className="rounded-pill px-4"
                          onClick={() => fetchTarifs(true)}
                          disabled={tarifsRefreshLoading}
                        >
                          <i className={`bi ${tarifsRefreshLoading ? 'bi-arrow-repeat spin' : 'bi-arrow-clockwise'} me-2`}></i>
                          Actualiser
                        </Button>
                      </div>
                    </div>

                    <Row className="mb-4">
                      <Col md={6}>
                        <InputGroup className="shadow-sm">
                          <InputGroup.Text className="bg-white border-end-0">
                            <i className="bi bi-search text-muted"></i>
                          </InputGroup.Text>
                          <Form.Control
                            type="text"
                            placeholder="Rechercher dans les tarifs..."
                            value={filterText}
                            onChange={e => setFilterText(e.target.value)}
                            className="border-start-0"
                          />
                          {filterText && (
                            <Button variant="outline-secondary" onClick={() => setFilterText('')}>
                              <i className="bi bi-x"></i>
                            </Button>
                          )}
                        </InputGroup>
                      </Col>
                      <Col md={6} className="text-end">
                        <Dropdown>
                          <Dropdown.Toggle variant="outline-secondary" className="rounded-pill px-4">
                            <i className="bi bi-list-ul me-2"></i>
                            {itemsPerPage} par page
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => setItemsPerPage(10)}>10 par page</Dropdown.Item>
                            <Dropdown.Item onClick={() => setItemsPerPage(25)}>25 par page</Dropdown.Item>
                            <Dropdown.Item onClick={() => setItemsPerPage(50)}>50 par page</Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </Col>
                    </Row>

                    <div className="table-responsive bg-white rounded-4 shadow-sm p-3">
                      <Table hover className="mb-0">
                        <thead className="bg-primary text-white">
                          <tr>
                            <th style={{ width: '40px' }}>
                              <Form.Check
                                type="checkbox"
                                checked={isAllSelected}
                                onChange={handleSelectAll}
                                className="custom-checkbox"
                              />
                            </th>
                            <SortableHeader field="reference">RÉFÉRENCE</SortableHeader>
                            <SortableHeader field="type">TYPE</SortableHeader>
                            <SortableHeader field="country">PAYS</SortableHeader>
                            <SortableHeader field="network">RÉSEAU</SortableHeader>
                            <SortableHeader field="sms_pricing">PRIX SMS</SortableHeader>
                            <SortableHeader field="currency">DEVISE</SortableHeader>
                            <SortableHeader field="statut">STATUT</SortableHeader>
                          </tr>
                        </thead>
                        <tbody>
                          {tarifsLoading ? (
                            <tr>
                              <td colSpan="8" className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-3 text-muted mb-0">Chargement des tarifs...</p>
                              </td>
                            </tr>
                          ) : currentItems.length > 0 ? (
                            currentItems.map((tarif, index) => (
                              <tr key={tarif.id} className="align-middle">
                                <td>
                                  <Form.Check
                                    type="checkbox"
                                    checked={selectedTarifs.includes(tarif.id)}
                                    onChange={() => handleSelectTarif(tarif.id)}
                                  />
                                </td>
                                <td><code className="bg-light p-2 rounded">{tarif.reference}</code></td>
                                <td><span className="badge bg-info text-white px-3 py-2">{tarif.type}</span></td>
                                <td><span className="badge bg-light text-dark border px-3 py-2">{tarif.country}</span></td>
                                <td><span className="badge bg-secondary text-white px-3 py-2">{tarif.network}</span></td>
                                <td><strong className="text-primary fs-6">{tarif.sms_pricing}</strong></td>
                                <td><span className="badge bg-light text-dark border px-3 py-2">{tarif.currency}</span></td>
                                <td><span className="badge bg-success px-3 py-2">{tarif.statut}</span></td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="8" className="text-center py-4">
                                <i className="bi bi-currency-exchange display-4 text-muted"></i>
                                <p className="mt-3 text-muted">Aucun tarif trouvé</p>
                                {filterText && (
                                  <Button variant="outline-primary" size="sm" onClick={() => setFilterText('')}>
                                    Réinitialiser la recherche
                                  </Button>
                                )}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {!tarifsLoading && totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="text-muted small">
                          Affichage de {startIndex + 1} à {Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)} sur {filteredAndSortedItems.length} tarifs
                        </div>
                        <Pagination className="mb-0">
                          <Pagination.Prev 
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(p => p - 1)}
                          />
                          {[...Array(totalPages)].map((_, i) => (
                            <Pagination.Item
                              key={i+1}
                              active={i+1 === currentPage}
                              onClick={() => setCurrentPage(i+1)}
                            >
                              {i+1}
                            </Pagination.Item>
                          ))}
                          <Pagination.Next 
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(p => p + 1)}
                          />
                        </Pagination>
                      </div>
                    )}
                  </div>
                </Tab>

                {/* Onglet Recharge SMS */}
                <Tab
                  eventKey="recharge"
                  title={
                    <div className="d-flex align-items-center px-3 py-2">
                      <i className="bi bi-envelope-paper me-2"></i>
                      <span>Recharge Solde</span>
                    </div>
                  }
                >
                  <div className="p-5" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)' }}>
                    <Row className="justify-content-center">
                      <Col lg={10}>
                        <div className="bg-white p-5 rounded-4 shadow-lg">
                          <div className="text-center py-4">
                            <div className="mb-3">
                              <div className="bg-light rounded-circle d-inline-flex p-3 mb-2">
                                <i className="bi bi-envelope-paper fs-1 text-primary"></i>
                              </div>
                              <h5 className="mb-2" style={{ color: "#1a1a2e" }}>Recharge en SMS</h5>
                              <p className="text-muted mb-3" style={{ fontSize: "13px" }}>
                                Indiquez le nombre exact de SMS que vous souhaitez ajouter au compte
                              </p>
                            </div>
                            <Row className="justify-content-center">
                              <Col md={6}>
                                <InputGroup className="shadow-sm mb-3" style={{ borderRadius: "10px", overflow: "hidden" }}>
                                  <InputGroup.Text className="bg-primary text-white border-0">
                                    <i className="bi bi-envelope-paper"></i>
                                  </InputGroup.Text>
                                  <Form.Control
                                    type="text"
                                    placeholder="Exemple: 10000"
                                    value={nombreSMSInput}
                                    onChange={handleNombreSMSChange}
                                    className="py-3 border-0"
                                    disabled={rechargeLoading}
                                    style={{ fontSize: "16px" }}
                                  />
                                  <InputGroup.Text className="bg-light border-0 fw-bold">
                                    SMS
                                  </InputGroup.Text>
                                </InputGroup>
                              </Col>
                            </Row>
                          </div>

                          {notification.show && (
                            <Alert variant={notification.type} className="rounded-3 py-3">
                              <i className={`bi ${notification.type === 'success' ? 'bi-check-circle-fill' : 'bi-info-circle-fill'} me-2`}></i>
                              {notification.message}
                            </Alert>
                          )}

                          <div className="text-center mt-5">
                            <Button
                              variant="primary"
                              size="lg"
                              className="px-5 py-3 rounded-pill shadow-lg"
                              style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none', minWidth: '250px' }}
                              disabled={
                                !nombreSMSInput || 
                                parseInt(nombreSMSInput) <= 0 ||
                                rechargeLoading
                              }
                              onClick={handleRechargeSolde}
                            >
                              {rechargeLoading ? (
                                <>
                                  <Spinner animation="border" size="sm" className="me-2" />
                                  Traitement en cours...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-lightning-charge-fill me-2"></i>
                                  Ajouter {nombreSMSInput || 0} SMS
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Tab>

                {/* Onglet Recharge Crédit V2 */}
                <Tab
                  eventKey="recharge-credit"
                  title={
                    <div className="d-flex align-items-center px-3 py-2">
                      <i className="bi bi-coin me-2"></i>
                      <span>Recharge Solde Crédit</span>
                    </div>
                  }
                >
                  <div className="p-5" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)' }}>
                    <Row className="justify-content-center">
                      <Col lg={10}>
                        <div className="bg-white p-5 rounded-4 shadow-lg">
                          <div className="text-center py-4">
                            <div className="mb-3">
                              <div className="bg-light rounded-circle d-inline-flex p-3 mb-2">
                                <i className="bi bi-coin fs-1 text-success"></i>
                              </div>
                              <h5 className="mb-2" style={{ color: "#1a1a2e" }}>Recharge en Crédit</h5>
                              <p className="text-muted mb-3" style={{ fontSize: "13px" }}>
                                Indiquez le montant que vous souhaitez ajouter au compte
                              </p>
                            </div>
                            <Row className="justify-content-center">
                              <Col md={6}>
                                <InputGroup className="shadow-sm mb-3" style={{ borderRadius: "10px", overflow: "hidden" }}>
                                  <InputGroup.Text className="bg-success text-white border-0">
                                    <i className="bi bi-coin"></i>
                                  </InputGroup.Text>
                                  <Form.Control
                                    type="text"
                                    placeholder="Exemple: 10000"
                                    value={montantInput}
                                    onChange={handleMontantChange}
                                    className="py-3 border-0"
                                    disabled={rechargeCreditLoading}
                                    style={{ fontSize: "16px" }}
                                  />
                                  <InputGroup.Text className="bg-light border-0 fw-bold">
                                  </InputGroup.Text>
                                </InputGroup>
                              </Col>
                            </Row>
                          </div>

                          {notification.show && (
                            <Alert variant={notification.type} className="rounded-3 py-3">
                              <i className={`bi ${notification.type === 'success' ? 'bi-check-circle-fill' : 'bi-info-circle-fill'} me-2`}></i>
                              {notification.message}
                            </Alert>
                          )}

                          <div className="text-center mt-5">
                            <Button
                              variant="success"
                              size="lg"
                              className="px-5 py-3 rounded-pill shadow-lg"
                              style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', border: 'none', minWidth: '250px' }}
                              disabled={
                                !montantInput || 
                                parseFloat(montantInput) <= 0 ||
                                rechargeCreditLoading
                              }
                              onClick={handleRechargeCredit}
                            >
                              {rechargeCreditLoading ? (
                                <>
                                  <Spinner animation="border" size="sm" className="me-2" />
                                  Traitement en cours...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-lightning-charge-fill me-2"></i>
                                  Ajouter {montantInput || 0} 
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Tab>

                {/* ================ NOUVEL ONGLET : HISTORIQUE SMS ================ */}
                <Tab
                  eventKey="sms-history"
                  title={
                    <div className="d-flex align-items-center px-3 py-2">
                      <i className="bi bi-clock-history me-2"></i>
                      <span>Historique SMS</span>
                    </div>
                  }
                >
                  <div className="p-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="fw-bold mb-0">
                        <i className="bi bi-clock-history me-2 text-primary"></i>
                        Derniers SMS de l'entité
                      </h5>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={fetchSmsHistory}
                        disabled={smsHistoryLoading}
                      >
                        <i className={`bi ${smsHistoryLoading ? 'bi-arrow-repeat spin' : 'bi-arrow-clockwise'} me-2`}></i>
                        Actualiser
                      </Button>
                    </div>

                    {smsHistoryLoading ? (
                      <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">Chargement des SMS...</p>
                      </div>
                    ) : smsHistoryError ? (
                      <Alert variant="danger">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {smsHistoryError}
                      </Alert>
                    ) : smsHistory.length === 0 ? (
                      <div className="text-center py-5">
                        <i className="bi bi-inbox display-4 text-muted"></i>
                        <p className="mt-3 text-muted">Aucun SMS trouvé pour cette entité</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table hover className="mb-0">
                          <thead className="bg-primary text-white">
                            <tr>
                              <th>#</th>
                              <th>Message</th>
                              <th>Destinataire</th>
                              <th>Expéditeur</th>
                              <th>Date d'envoi</th>
                              <th>Statut</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {smsHistory.map((sms, index) => {
                              const statusBadge = getStatusBadge(sms.sms_status);
                              return (
                                <tr key={sms.id || index}>
                                  <td>{index + 1}</td>
                                  <td>
                                    <div 
                                      className="text-truncate" 
                                      style={{ maxWidth: '200px' }}
                                      title={sms.message}
                                    >
                                      {sms.message || 'N/A'}
                                    </div>
                                  </td>
                                  <td>
                                    <code className="bg-light p-1 rounded">{sms.msisdn}</code>
                                  </td>
                                  <td>
                                    <Badge bg="info" className="px-2 py-1">
                                      {sms.sender || 'N/A'}
                                    </Badge>
                                  </td>
                                  <td>
                                    <small>{formatSmsDate(sms.send_date)}</small>
                                  </td>
                                  <td>
                                    <Badge bg={statusBadge.variant}>
                                      {statusBadge.label}
                                    </Badge>
                                  </td>
                                  <td>
                                    <Button 
                                      variant="outline-primary" 
                                      size="sm"
                                      onClick={() => {
                                        setSelectedSms(sms);
                                        setShowSmsDetailModal(true);
                                      }}
                                    >
                                      <i className="bi bi-eye"></i>
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </div>
                </Tab>
              </Tabs>
            </Card.Header>
          </Card>
        </Container>

        {/* Modals pour Recharge SMS */}
        <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered className="modal-custom">
          <Modal.Body className="text-center py-5">
            <i className="bi bi-check-circle-fill display-1 text-success mb-3"></i>
            <h4 className="fw-bold text-success mb-3">Recharge SMS effectuée !</h4>
            <p className="mb-2">
              <span className="fw-bold text-success fs-4">{smsQuantity || 0}</span> SMS ont été ajoutés au compte.
            </p>
            {responseData?.data && (
              <div className="bg-light p-3 rounded-3 my-3">
                <p className="mb-1">
                  <strong>Ancien solde :</strong> 
                  <span className="text-muted ms-2">{responseData.data.ancien_solde || 0} SMS</span>
                </p>
                <p className="mb-0">
                  <strong>Nouveau solde :</strong> 
                  <span className="text-success fw-bold ms-2">{responseData.data.nouveau_solde || 0} SMS</span>
                </p>
              </div>
            )}
            <Button variant="success" onClick={() => setShowSuccessModal(false)} className="rounded-pill px-5 py-2 mt-3">
              <i className="bi bi-check-lg me-2"></i> OK
            </Button>
          </Modal.Body>
        </Modal>

        <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered className="modal-custom">
          <Modal.Body className="text-center py-5">
            <i className="bi bi-exclamation-triangle-fill display-1 text-danger mb-3"></i>
            <h4 className="fw-bold text-danger mb-3">Erreur de recharge SMS</h4>
            <p className="mb-4">{errorMessage || "Une erreur est survenue lors de la recharge."}</p>
            <Button variant="danger" onClick={() => setShowErrorModal(false)} className="rounded-pill px-5 py-2">
              <i className="bi bi-arrow-left me-2"></i> Retour
            </Button>
          </Modal.Body>
        </Modal>

        {/* Modals pour Recharge Crédit V2 */}
        <Modal show={showCreditSuccessModal} onHide={() => setShowCreditSuccessModal(false)} centered className="modal-custom">
          <Modal.Body className="text-center py-5">
            <i className="bi bi-check-circle-fill display-1 text-success mb-3"></i>
            <h4 className="fw-bold text-success mb-3">Recharge Crédit effectuée !</h4>
            <p className="mb-2">
              <span className="fw-bold text-success fs-4">{creditMontant || 0}</span> ont été ajoutés au compte.
            </p>
            {creditSuccessMessage && (
              <div className="bg-light p-3 rounded-3 my-3">
                <p className="mb-0 text-success fw-semibold">
                  <i className="bi bi-info-circle me-2"></i>
                  {creditSuccessMessage}
                </p>
              </div>
            )}
            {creditResponseData?.nouveau_solde !== undefined && (
              <div className="bg-light p-3 rounded-3 my-3">
                <p className="mb-0">
                  <strong>Nouveau solde :</strong> 
                  <span className="text-success fw-bold ms-2">{creditResponseData.nouveau_solde}</span>
                </p>
              </div>
            )}
            <Button variant="success" onClick={() => setShowCreditSuccessModal(false)} className="rounded-pill px-5 py-2 mt-3">
              <i className="bi bi-check-lg me-2"></i> OK
            </Button>
          </Modal.Body>
        </Modal>

        <Modal show={showCreditErrorModal} onHide={() => setShowCreditErrorModal(false)} centered className="modal-custom">
          <Modal.Body className="text-center py-5">
            <i className="bi bi-exclamation-triangle-fill display-1 text-danger mb-3"></i>
            <h4 className="fw-bold text-danger mb-3">Erreur de recharge Crédit</h4>
            <div className="bg-light p-3 rounded-3 my-3">
              <p className="mb-0 text-danger">
                <i className="bi bi-info-circle me-2"></i>
                {creditErrorMessage || "Une erreur est survenue lors de la recharge crédit."}
              </p>
            </div>
            <Button variant="danger" onClick={() => setShowCreditErrorModal(false)} className="rounded-pill px-5 py-2 mt-3">
              <i className="bi bi-arrow-left me-2"></i> Retour
            </Button>
          </Modal.Body>
        </Modal>

        {/* Modal de confirmation KYC */}
        <Modal show={showKycModal} onHide={() => setShowKycModal(false)} centered className="modal-custom">
          <Modal.Header closeButton className={`${kycAction === 'activate' ? 'bg-success' : 'bg-danger'} text-white`}>
            <Modal.Title>
              <i className={`bi ${kycAction === 'activate' ? 'bi-shield-check' : 'bi-shield-slash'} me-2`}></i>
              {kycAction === 'activate' ? 'Activation KYC' : 'Désactivation KYC'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="py-4">
            <div className="text-center">
              <div className={`mb-4 ${kycAction === 'activate' ? 'text-success' : 'text-danger'}`}>
                <i className={`bi ${kycAction === 'activate' ? 'bi-shield-check' : 'bi-shield-slash'} display-1`}></i>
              </div>
              <h5 className="fw-bold mb-3">
                {kycAction === 'activate' 
                  ? 'Confirmer l\'activation du KYC' 
                  : 'Confirmer la désactivation du KYC'}
              </h5>
              <p className="text-muted mb-4">
                {kycAction === 'activate' 
                  ? `Êtes-vous sûr de vouloir activer le KYC pour l'entité "${entite?.business_name}" ? Cette action permettra à l'entité d'envoyer des SMS.`
                  : `Êtes-vous sûr de vouloir désactiver le KYC pour l'entité "${entite?.business_name}" ? Cette action bloquera l'envoi de SMS.`
                }
              </p>
              <div className="bg-light p-3 rounded-3 mb-4">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted">Statut actuel :</span>
                  <Badge bg={entite?.kyc === 'OK' ? 'success' : 'warning'} className="px-3 py-2">
                    {entite?.kyc === 'OK' ? ' Activé' : ' En attente'}
                  </Badge>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <span className="text-muted">Nouveau statut :</span>
                  <Badge bg={kycAction === 'activate' ? 'success' : 'danger'} className="px-3 py-2">
                    {kycAction === 'activate' ? ' Activé' : ' Désactivé'}
                  </Badge>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="secondary" onClick={() => setShowKycModal(false)} disabled={kycLoading}>
              <i className="bi bi-x-circle me-2"></i>
              Annuler
            </Button>
            <Button 
              variant={kycAction === 'activate' ? 'success' : 'danger'}
              onClick={handleUpdateKyc}
              disabled={kycLoading}
              className="px-4"
            >
              {kycLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Traitement...
                </>
              ) : (
                <>
                  <i className={`bi ${kycAction === 'activate' ? 'bi-shield-check' : 'bi-shield-slash'} me-2`}></i>
                  {kycAction === 'activate' ? 'Activer KYC' : 'Désactiver KYC'}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal d'édition */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title>
              <i className="bi bi-pencil me-2"></i>
              Modifier l'entité
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nom de l'entreprise</Form.Label>
                  <Form.Control
                    value={editForm.business_name || ''}
                    onChange={(e) => setEditForm({...editForm, business_name: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={editForm.business_email || ''}
                    onChange={(e) => setEditForm({...editForm, business_email: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Téléphone</Form.Label>
                  <Form.Control
                    value={editForm.phone || ''}
                    onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Pays</Form.Label>
                  <Form.Select
                    value={editForm.business_country || 'GA'}
                    onChange={(e) => setEditForm({...editForm, business_country: e.target.value})}
                  >
                    <option value="GA">Gabon</option>
                    <option value="SN">Sénégal</option>
                    <option value="CI">Côte d'Ivoire</option>
                    <option value="CM">Cameroun</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ville</Form.Label>
                  <Form.Control
                    value={editForm.business_city || ''}
                    onChange={(e) => setEditForm({...editForm, business_city: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ident Header</Form.Label>
                  <Form.Control
                    maxLength={255}
                    value={editForm.x_header_client || ''}
                    onChange={(e) => setEditForm({...editForm, x_header_client: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={editLoading}>Annuler</Button>
            <Button variant="primary" onClick={handleSaveEdit} disabled={editLoading}>
              {editLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Enregistrement...
                </>
              ) : 'Enregistrer'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ================ MODAL DÉTAILS SMS ================ */}
        <Modal 
          show={showSmsDetailModal} 
          onHide={() => setShowSmsDetailModal(false)}
          centered
          size="lg"
        >
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title>
              <i className="bi bi-envelope-paper me-2"></i>
              Détails du SMS
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedSms && (
              <div>
                <Row className="mb-3">
                  <Col md={6}>
                    <div className="bg-light p-3 rounded">
                      <strong>Destinataire :</strong>
                      <p className="mb-0">{selectedSms.msisdn}</p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="bg-light p-3 rounded">
                      <strong>Expéditeur :</strong>
                      <p className="mb-0">{selectedSms.sender || 'N/A'}</p>
                    </div>
                  </Col>
                </Row>
                
                <div className="bg-light p-3 rounded mb-3">
                  <strong>Message :</strong>
                  <p className="mb-0 mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                    {selectedSms.message || 'Contenu non disponible'}
                  </p>
                </div>
                
                <Row>
                  <Col md={4}>
                    <div className="bg-light p-2 rounded text-center">
                      <strong>Statut</strong>
                      <p className="mb-0">
                        <Badge bg={getStatusBadge(selectedSms.sms_status).variant}>
                          {getStatusBadge(selectedSms.sms_status).label}
                        </Badge>
                      </p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="bg-light p-2 rounded text-center">
                      <strong>Date d'envoi</strong>
                      <p className="mb-0 small">{formatSmsDate(selectedSms.send_date)}</p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="bg-light p-2 rounded text-center">
                      <strong>Nombre de SMS</strong>
                      <p className="mb-0">{selectedSms.nb_sms || 1}</p>
                    </div>
                  </Col>
                </Row>
                
                {selectedSms.external_id && (
                  <div className="mt-3">
                    <small className="text-muted">
                      <strong>ID externe :</strong> {selectedSms.external_id}
                    </small>
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSmsDetailModal(false)}>
              Fermer
            </Button>
          </Modal.Footer>
        </Modal>

        <Footer />
      </div>

      <style>{`
        .text-gradient-primary {
          background: linear-gradient(135deg, #0d6efd 0%, #6610f2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .breadcrumb-arrow {
          --bs-breadcrumb-divider: '›';
        }
        
        .icon-statistic {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .statistic-wave {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 100px;
          height: 100px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          transform: translate(30%, 30%);
        }
        
        .icon-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .custom-tabs .nav-link {
          border: none;
          color: #6c757d;
          font-weight: 500;
          padding: 0;
          margin-right: 1rem;
        }
        
        .custom-tabs .nav-link.active {
          color: #0d6efd;
          background: none;
          border-bottom: 3px solid #0d6efd;
        }
        
        .list-group-custom .list-group-item {
          border: none;
          border-bottom: 1px solid #f1f3f9;
        }
        
        .list-group-custom .list-group-item:last-child {
          border-bottom: none;
        }
        
        .cursor-pointer {
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .cursor-pointer:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
        
        .spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .modal-custom .modal-content {
          border-radius: 20px;
          overflow: hidden;
        }

        .custom-checkbox .form-check-input {
          width: 20px;
          height: 20px;
          cursor: pointer;
          border-radius: 4px;
        }

        .custom-checkbox .form-check-input:checked {
          background-color: #0d6efd;
          border-color: #0d6efd;
        }

        .bg-gradient {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
      `}</style>
    </>
  );
}